/**
 * Client-side router over the HTML5 History API. Carried across from v1 (the shape
 * worked well) with one change: navigation is guarded by an async `beforeNavigate`
 * that can redirect, and unknown paths fall back to a registered 404 handler rather
 * than bouncing straight to /login.
 */
class Router {
    constructor() {
        this.routes = new Map();
        // Only routes containing ':' land here. Exact matches are still resolved by the
        // Map first, so adding parameters costs existing routes nothing -- neither a
        // behaviour change nor a scan.
        this.patterns = [];
        this.notFoundHandler = null;
        this.beforeNavigate = null;
        this.beforeRender = null;
        this.afterNavigate = null;
        this.onError = null;
        this.currentPath = null;

        window.addEventListener('popstate', () => {
            this.navigate(window.location.pathname, { push: false });
        });

        // Any <a data-link> is routed internally instead of reloading the page.
        // closest() so clicks on an icon or span inside the link still work.
        document.addEventListener('click', (e) => {
            const link = e.target.closest?.('a[data-link]');
            if (!link) return;
            e.preventDefault();
            this.navigate(link.getAttribute('href'));
        });
    }

    route(path, handler) {
        this.routes.set(path, handler);
        if (path.includes(':')) this.patterns.push(compilePattern(path, handler));
        return this;
    }

    setNotFound(handler) {
        this.notFoundHandler = handler;
    }

    setBeforeNavigate(cb) {
        this.beforeNavigate = cb;
    }

    /** Draws persistent chrome (the sidebar) before the page handler runs. */
    setBeforeRender(cb) {
        this.beforeRender = cb;
    }

    /** Called when a route handler throws, so a broken page is visible rather than silent. */
    setOnError(cb) {
        this.onError = cb;
    }

    setAfterNavigate(cb) {
        this.afterNavigate = cb;
    }

    async navigate(path, { push = true, replace = false } = {}) {
        const url = new URL(path, window.location.origin);
        const pathname = url.pathname;

        if (this.beforeNavigate) {
            const decision = await this.beforeNavigate(pathname);
            // A string redirects; false cancels; anything else proceeds.
            if (decision === false) return;
            if (typeof decision === 'string' && decision !== pathname) {
                return this.navigate(decision, { replace: true });
            }
        }

        if (replace) {
            window.history.replaceState(null, '', path);
        } else if (push && pathname + url.search !== window.location.pathname + window.location.search) {
            window.history.pushState(null, '', path);
        }

        // Exact first, so a literal route always wins over a pattern that could also
        // match it -- '/events/new' must never be swallowed by '/events/:id'.
        let handler = this.routes.get(pathname);
        let params = {};
        if (!handler) {
            const matched = matchPattern(this.patterns, pathname);
            if (matched) {
                handler = matched.handler;
                params = matched.params;
            }
        }
        handler = handler ?? this.notFoundHandler;
        if (!handler) return;

        this.currentPath = pathname;
        this.params = params;

        // Chrome is drawn *before* the page handler and again after. Before, so the
        // sidebar is present immediately and survives a handler that throws -- an error
        // mid-render used to leave the page with content but no navigation, strandable
        // only by clicking Back. After, so it reflects any state the handler changed.
        this.beforeRender?.(pathname);

        try {
            await handler(params);
        } catch (err) {
            console.error(`Route ${pathname} failed to render`, err);
            this.onError?.(err, pathname);
        } finally {
            this.afterNavigate?.(pathname);
        }
    }

    /** Re-runs the current route, e.g. after a mutation that changes what it renders. */
    reload() {
        return this.navigate(window.location.pathname + window.location.search, { push: false });
    }

    getCurrentPath() {
        return window.location.pathname;
    }
}

/**
 * Splits '/events/:id' into segments once, at registration, rather than on every
 * navigation. Nothing here supports wildcards or optional segments: the two routes that
 * need parameters need exactly one each, and a fuller matcher would be speculative.
 */
function compilePattern(path, handler) {
    return { segments: path.split('/').filter(Boolean), handler };
}

function matchPattern(patterns, pathname) {
    const parts = pathname.split('/').filter(Boolean);

    for (const { segments, handler } of patterns) {
        if (segments.length !== parts.length) continue;

        const params = {};
        let ok = true;
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            if (segment.startsWith(':')) {
                params[segment.slice(1)] = decodeURIComponent(parts[i]);
            } else if (segment !== parts[i]) {
                ok = false;
                break;
            }
        }
        if (ok) return { handler, params };
    }
    return null;
}

export const router = new Router();
